import { Component, ContentChild, TemplateRef, Directive } from '@angular/core';
import { DomAttributesService } from '@backbase/ui-ang/services';

/**
 * Directive for custom modal body content template.
 *
 * @remarks
 * Allows embedding custom content within modal body using Angular's content projection.
 * The directive wraps template content and provides access to the template reference for rendering.
 *
 * @example
 *
 * ```html
 * <bb-modal-body-ui>
 *   <ng-template bbCustomModalBody>
 *     This website uses cookies to enhance your browsing experience.
 *   </ng-template>
 * </bb-modal-body-ui>
 * ```
 */
@Directive({
  selector: 'ng-template[bbCustomModalBody]',
  standalone: false,
})
export class CustomModalBodyDirective {
  constructor(public templateRef: TemplateRef<any>) {}
}

/**
 * Modal body component that provides content area structure for modal dialogs.
 *
 * @remarks
 * Handles modal body content with optional custom templates and scrollable configuration.
 * Automatically generates accessibility IDs and manages scrollable behavior based on modal options.
 * Works in conjunction with modal header and footer components to create complete modal structures.
 *
 * @example
 *
 * ```html
 * <bb-modal-body-ui>
 *   <ng-template bbCustomModalBody>
 *     This website uses cookies to enhance your browsing experience.
 *   </ng-template>
 * </bb-modal-body-ui>
 * ```
 */
@Component({
  selector: 'bb-modal-body-ui',
  templateUrl: './modal-body.component.html',
  standalone: false,
})
export class ModalBodyComponent {
  /**
   * Custom modal body directive containing user-provided template content.
   */
  @ContentChild(CustomModalBodyDirective, { static: true }) customModalBody: CustomModalBodyDirective | undefined;

  /**
   * Reference to this component instance for template context.
   */
  readonly hostRef: ModalBodyComponent = this;

  /**
   * Unique identifier for the modal body element.
   *
   * @example
   *
   * ```html
   * <div class="modal-body" [id]="bodyId"></div>
   * ```
   */
  public readonly bodyId = this.domAttrService.generateId();

  private _scrollable: boolean | undefined;

  /**
   * Sets the scrollable state for the modal body.
   *
   * @remarks
   * Controls whether the modal body can be scrolled and affects the tabIndex attribute
   * for accessibility. When scrollable, the modal body becomes focusable with tabIndex="0".
   *
   * @param scrollable - Whether the modal body should be scrollable
   *
   * @example
   *
   * ```typescript
   * this.modalBody?.setScrollable(this.modalOptions.scrollable);
   * ```
   */
  setScrollable(scrollable: boolean | undefined): void {
    this._scrollable = scrollable;
  }

  /**
   * Current scrollable state of the modal body.
   *
   * @example
   *
   * ```html
   * <div class="modal-body" [attr.tabIndex]="scrollable ? '0' : null">
   * ```
   */
  get scrollable(): boolean | undefined {
    return this._scrollable;
  }

  constructor(private readonly domAttrService: DomAttributesService) {}
}
